import { addMonths, compareAsc, isBefore } from 'date-fns';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ONBOARDING_STEP_EVENT } from '../../../constants/analyticEvents';
import { pushEvent } from '../../../utilities/analytics';
import format from '../../../utilities/format';
import { isMobile, parseDate } from '../../../utilities/helpers';
import { setCurrentStep, setDataV2 } from '../onboardingSlice';
import WizardDateButton from '../components/WizardDateButton';
import WizardViewMoreButton from '../components/WizardViewMoreButton';
import WizardXpButton from '../components/WizardXpButton';
import { getWizardV2State } from '../wizardService';
import WhenStepDesktop from '../../wizardDesktop/steps/WhenStepDesktop';

function getUpcomingMonths(count) {
  let upcomingMonths = [];
  for (let i = 0; i < count; i++) {
    upcomingMonths.push(addMonths(new Date(), i));
  }

  return upcomingMonths;
}

const WhenStep = () => {
  const dispatch = useDispatch();
  const { formV2: form, step } = useSelector(state => state.onboarding);
  const currentDate = new Date();
  let currentMonth = currentDate.getMonth() + 1;
  const [showMore, setShowMore] = useState(
    (form.when.months.length > 0 && form.when.months.some((month) => (+month < currentMonth || +month > currentMonth+5))) ||
    form.when.customHoliday !== null ||
    form.when.lastMinuteDeals
  );
  const { when } = form;
  const stepConfig = getWizardV2State().whenStep;
  const months = getUpcomingMonths(stepConfig.maxMonthsAhead);
  const [topHolidays, bottomHolidays] = getSortedHolidays();

  const monthsToShow = months.slice(0, showMore ? months.length : 6);

  function getSortedHolidays() {
    const holidays = stepConfig.holidays
      .sort((a, b) => compareAsc(parseDate(a.dateFrom, 'dd-MM-yyyy'), parseDate(b.dateFrom, 'dd-MM-yyyy')))
      .filter(holiday => isBefore(new Date(), parseDate(holiday.dateTo, 'dd-MM-yyyy')))
      .filter(holiday => holiday.active);

    let stickedTopHolidays = holidays.filter(holiday => holiday.stickedTop);
    let stickedBottomHolidays = holidays.filter(holiday => !holiday.stickedTop);

    if (stickedTopHolidays.length === 0 && stickedBottomHolidays.length) {
      const [firstHoliday, ...restHolidays] = stickedBottomHolidays;
      stickedTopHolidays = [firstHoliday];
      stickedBottomHolidays = restHolidays;
    }

    return [stickedTopHolidays, stickedBottomHolidays];
  }

  const onMonthClick = (month) => {
    let newMonths = [...when.months];

    if (when.months.includes(month)) {
      newMonths = when.months.filter(item => item !== month);
    } else {
      newMonths = [...newMonths, month];
    }

    dispatch(setDataV2({
      when: {
        months: newMonths,
        customHoliday: null,
        lastMinuteDeals: false,
      }
    }))
  }

  const onCustomHolidayClick = (holiday) => {
    dispatch(setDataV2({
      when: {
        months: [],
        customHoliday: form.when.customHoliday?.text === holiday.text ? null : holiday,
        lastMinuteDeals: false,
      }
    }))
  }

  const onLastMinuteDealsClick = () => {
    dispatch(setDataV2({
      when: {
        months: [],
        customHoliday: [],
        lastMinuteDeals: true,
      }
    }));
  }

  const handleContinueClick = () => {
    dispatch(setCurrentStep(1))
    const eventData = {
      wizardV2: true,
      question: stepConfig.title,
      selection: form.when,
      skipAvailable: !!(localStorage.getItem('onboardingCompleted')),
      step,
      stepName: 'when',
      departure: form.when.customHoliday?.dateFrom,
      return: form.when.customHoliday?.dateTo,
    };

    pushEvent(ONBOARDING_STEP_EVENT, eventData);
  }

  if (!isMobile()) {
    return (
      <WhenStepDesktop
        months={months}
        form={form}
        onMonthClick={onMonthClick}
        holidays={getSortedHolidays()}
        onCustomHolidayClick={onCustomHolidayClick}
        when={when}
        onContinueClick={handleContinueClick}
        onLastMinuteDealsClick={onLastMinuteDealsClick}
      />
    )
  }

  return (
    <div className="wizard-content-scrollable">
      <div className="wizard-step">
        <div className="wizard-step__title">{stepConfig.title}</div>
        <div className="wizard-step__subtitle">{stepConfig.subTitle}</div>
        <div className="wizard-step__options wizard-when-step">
          {stepConfig.lastMinuteStickedTop && (
            <WizardXpButton
              label={stepConfig.lastMinuteTitle}
              subtext={stepConfig.lastMinuteSubTitle}
              iconUrl="https://d16tr0byigrcd.cloudfront.net/hf/images/wizard/icon-lastminute.svg"
              className="wizard-lastminute-icon"
              onClick={onLastMinuteDealsClick}
              active={when.lastMinuteDeals}
            />
          )}
          {!stepConfig.lastMinuteStickedTop && topHolidays && topHolidays.map((holiday, index) => (
            <WizardXpButton
              key={index}
              label={holiday.text}
              subtext={format(parseDate(holiday.dateFrom, 'dd-MM-yyyy'), 'yyyy')}
              iconUrl={holiday.icon}
              onClick={() => onCustomHolidayClick(holiday)}
              active={when.customHoliday?.text === holiday.text}
              className="slide-in-bottom animation-delay-0"
            />
          ))}
          <div className="wizard-when-step__months slide-in-bottom">
            {monthsToShow.map((month) => (
              <WizardDateButton
                key={format(month, 'MMM')}
                label={format(month, 'MMM')}
                subtext={format(month, 'yyyy')}
                onClick={() => onMonthClick(format(month, 'M'))}
                active={form.when.months.includes(format(month, 'M'))}
              />
            ))}
          </div>
          {showMore && bottomHolidays && (
            <>
              {stepConfig.lastMinuteStickedTop && topHolidays && topHolidays.map((holiday, index) => (
                <WizardXpButton
                  key={index}
                  label={holiday.text}
                  subtext={format(parseDate(holiday.dateFrom, 'dd-MM-yyyy'), 'yyyy')}
                  iconUrl={holiday.icon}
                  onClick={() => onCustomHolidayClick(holiday)}
                  active={when.customHoliday?.text === holiday.text}
                  className="slide-in-bottom animation-delay-0"
                />
              ))}
              {bottomHolidays.map((holiday, index) => (
                <WizardXpButton
                  key={index}
                  label={holiday.text}
                  subtext={format(parseDate(holiday.dateFrom, 'dd-MM-yyyy'), 'yyyy')}
                  iconUrl={holiday.icon}
                  onClick={() => onCustomHolidayClick(holiday)}
                  active={when.customHoliday?.text === holiday.text}
                  className={`slide-in-bottom animation-delay-${index * 50}`}
                />
              ))}
              {!stepConfig.lastMinuteStickedTop && (
                <WizardXpButton
                  label={stepConfig.lastMinuteTitle}
                  subtext={stepConfig.lastMinuteSubTitle}
                  iconUrl="https://d16tr0byigrcd.cloudfront.net/hf/images/wizard/icon-lastminute.svg"
                  className="wizard-lastminute-icon"
                  onClick={onLastMinuteDealsClick}
                  active={when.lastMinuteDeals}
                />
              )}
            </>
          )}
          {!showMore && (
            <WizardViewMoreButton
              expanded={showMore}
              onClick={() => setShowMore(!showMore)}
              label={stepConfig.viewMore}
              className="slide-in-bottom"
            />
          )}
        </div>
      </div>

      <div className="wizard-footer">
        <button className="wizard-control-button" onClick={handleContinueClick}>{stepConfig.buttonText}</button>
      </div>
    </div>
  )
}

export default WhenStep
